import { useEffect, useState } from 'preact/hooks';
import { listApprovals, updateApproval } from '../lib/api';
import { activeToken } from '../store/global';
import type { Approval } from '../types/agno-api';
import { PageHeader } from '../components/PageHeader';
import { DataTable } from '../components/DataTable';
import { formatRelative } from '../lib/format';

export function Approvals() {
  const [items, setItems] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!activeToken.value) return;
    setLoading(true);
    try {
      const res = await listApprovals();
      const data = Array.isArray(res) ? res : (res as { data: Approval[] }).data || [];
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [activeToken.value]);

  const act = async (id: string, status: 'approved' | 'rejected') => {
    await updateApproval(id, { status });
    refresh();
  };

  return (
    <div class="mx-auto max-w-7xl px-6 py-6">
      <PageHeader
        title="Aprovações"
        description="Ações sensíveis pausadas aguardando confirmação humana."
        icon={<i class="ph ph-shield-check text-xl" />}
        actions={
          <button
            class="inline-flex items-center justify-center gap-2 h-8 px-4 rounded-md text-sm font-medium bg-background text-foreground border border-input shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? 'Atualizando…' : 'Atualizar'}
          </button>
        }
      />
      <DataTable
        rows={items as unknown as Array<Record<string, unknown>>}
        rowKey={(r) => (r as unknown as Approval).id}
        columns={[
          {
            key: 'desc',
            label: 'Solicitação',
            render: (r) => {
              const a = r as unknown as Approval;
              return (
                <div>
                  <div class="font-medium">{a.description || a.tool_call?.name || a.id}</div>
                  {a.tool_call?.args && (
                    <pre class="text-xs font-mono tabular-nums mt-1 bg-muted border border-border rounded-md p-1.5 max-w-md overflow-auto max-h-32">
{JSON.stringify(a.tool_call.args, null, 2)}
                    </pre>
                  )}
                </div>
              );
            },
          },
          {
            key: 'status',
            label: 'Status',
            width: '12ch',
            render: (r) => {
              const s = (r as unknown as Approval).status;
              const tone = s === 'pending' ? 'text-warning' : s === 'approved' ? 'text-success' : 'text-muted-foreground';
              return <span class={`text-xs font-medium ${tone}`}>{s || '—'}</span>;
            },
          },
          { key: 'created', label: 'Criado', render: (r) => <span class="text-xs text-muted-foreground tabular-nums">{formatRelative((r as unknown as Approval).created_at)}</span>, width: '14ch' },
          {
            key: 'actions',
            label: '',
            width: '14ch',
            render: (r) => {
              const a = r as unknown as Approval;
              if (a.status !== 'pending') return null;
              return (
                <div class="flex items-center gap-1">
                  <button
                    class="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-md text-xs font-medium bg-success/10 text-success border border-success/20 hover:bg-success/20 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-success/40"
                    onClick={() => act(a.id, 'approved')}
                  >
                    <i class="ph ph-check text-sm" /> Aprovar
                  </button>
                  <button
                    class="inline-flex items-center justify-center gap-1 h-7 px-2 rounded-md text-xs font-medium bg-destructive/10 text-destructive border border-destructive/20 hover:bg-destructive/20 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40"
                    onClick={() => act(a.id, 'rejected')}
                  >
                    <i class="ph ph-x text-sm" /> Negar
                  </button>
                </div>
              );
            },
          },
        ]}
        empty={activeToken.value ? 'Nenhuma aprovação pendente. Ações sensíveis aparecem aqui quando aguardam confirmação.' : 'Configure o token nas configurações para listar aprovações.'}
      />
    </div>
  );
}
